// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — a brand kit's content, checked the same way in the browser,
 * on the server and in the shot (plan item J4): a logo as a data URL (PNG,
 * JPEG or WebP only — never SVG, which can carry scripts — up to
 * {@link BRAND_LOGO_MAX_CHARS}), a lower-third line up to 80 characters and an
 * accent colour as `#rrggbb`.
 */

export interface BrandContent {
  logo: string | null
  lowerThird: string | null
  accent: string
}

/** About 200 KB of image; the shot Khipu that carries it must stay under 256 KB. */
export const BRAND_LOGO_MAX_CHARS = 270_000
export const BRAND_TEXT_MAX = 80
export const DEFAULT_ACCENT = '#c0392b'
const LOGO = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/
const ACCENT = /^#[0-9a-fA-F]{6}$/

/** The content as stored and drawn, or null when the logo or colour is not acceptable. */
export function checkBrand(value: unknown): BrandContent | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const v = value as Record<string, unknown>
  const logo = typeof v.logo === 'string' && v.logo ? v.logo : null
  if (logo !== null && (logo.length > BRAND_LOGO_MAX_CHARS || !LOGO.test(logo))) return null
  const accent = typeof v.accent === 'string' && v.accent ? v.accent : DEFAULT_ACCENT
  if (!ACCENT.test(accent)) return null
  const lowerThird = typeof v.lowerThird === 'string' && v.lowerThird.trim() ? v.lowerThird.trim().slice(0, BRAND_TEXT_MAX) : null
  return { logo, lowerThird, accent: accent.toLowerCase() }
}
